/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,


	// Selectors
	selectors: {
		cart: "[data-cart], .mm-cart",
		cartWrapper: "[data-cart-wrapper]",
		cartProducts: "[data-cart-products]",
		cartBubbles: "[data-cart-count-bubble]",
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: "[data-cart-close]",
		checkoutBtn: "[data-checkout-btn]",
		drawerCart: "[data-cart]", // Use data-cart instead of data-drawer-cart
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: "[data-item-quantity]",
		freeProducts: "[data-free-products]",
	},

	// Development mode (uses global isDevelopment from scripts.liquid)
	isDevelopment: typeof isDevelopment !== "undefined" ? isDevelopment : false,
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const cartLog = (level, message, data = {}) => {
	if (!CART_CONFIG.isDevelopment && level === "info") return;
	if (typeof console !== "undefined") {
		const logFn = console[level] || console.log;
		logFn(`🛒 [CART] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Debounce function
const cartDebounce = (fn, delay) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.apply(this, args), delay);
	};
};

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: "cart",
	};

	return document.dispatchEvent(
		new CustomEvent(`cart:${event}`, {
			detail: eventData,
			cancelable,
			bubbles: true,
		})
	);
};

// DOM Selectors Cache
const DOM = {
	get cart() {
		return document.querySelector(CART_CONFIG.selectors.cart);
	},
	get cartSection() {
		return document.getElementById(this.cart?.id || "");
	},
	get cartProducts() {
		return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts);
	},
	get cartWrapper() {
		return document.querySelector(CART_CONFIG.selectors.cartWrapper);
	},
	get cartBubbles() {
		return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles);
	},

	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace("{{index}}", index)),

	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity),
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit("api-start", { method, path });

		const baseUrl = window.Shopify?.routes?.root || "/";
		const url = baseUrl + path;

		const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { "Content-Type": "application/json" };
		}

		if (body && method !== "GET") {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || "Shopify API Error");
			}

			cartEmit("api-success", { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit("api-error", { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Update cart count
		const count = data.item_count || 0;
		DOM.cartBubbles.forEach((bubble) => {
			bubble.classList.toggle("mm-none", count === 0);
			bubble.textContent = count || "";
		});
		cartEmit("count-updated", { count });

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace("shopify-section-", "");
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement("div");
		temp.innerHTML = html;

		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);

		if (newCart && currentCart) {
			currentCart.innerHTML = newCart.innerHTML;
			this.initCarousels();
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === "function") {
				window.initAddToCartForms();
			}

			// Réinitialiser le sticky CTA et mettre à jour les données
			if (typeof initStickyCTA === "function") {
				initStickyCTA();
			}

			// Mettre à jour la visibilité des boutons de suppression de code promo
			if (typeof updateDiscountRemoveButtons === "function") {
				updateDiscountRemoveButtons();
			}

			// Récupérer le prix et les codes promo du nouveau DOM et mettre à jour le sticky
			fetch("/cart.js")
				.then(r => r.json())
				.then(cartData => {
					if (typeof updateStickyPrice === "function") {
						updateStickyPrice(cartData.total_price);
					}
					if (typeof updateStickyCheckoutURL === "function") {
						updateStickyCheckoutURL(cartData.cart_level_discount_applications || []);
					}
				})
				.catch(err => cartLog("error", "Failed to update sticky CTA", { error: err.message }));

			cartEmit("refreshed");
		}
	},

	/**
	 * Reinitialize carousels after DOM refresh using the global SplideManager
	 */
	initCarousels() {
		// Use the global SplideManager from mm_components.js instead of custom logic
		if (window.SplideManager && typeof window.SplideManager.init === 'function') {
			window.SplideManager.init();
		} else {
			cartLog("warn", "SplideManager not available, carousels may not work properly");
		}
	},
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	async get() {
		cartEmit("fetch-start");
		const data = await CartAPI.request("GET", "cart.js");
		cartEmit("fetched", { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open() {
		if (!cartEmit("before-open", {}, true)) return;

		this.get().catch((error) => cartLog("error", "Failed to fetch cart on open", { error: error.message }));

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.classList.add("mm-dc-is-open");
			html.classList.add("stop-scrolling");

			// Hide overlays
			["shopify-chat", "axeptio_overlay"].forEach((id) => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = "none";
			});

			cartEmit("opened");
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit("before-close", {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper) wrapper.classList.remove("mm-dc-is-open");
		if (html) html.classList.remove("stop-scrolling");

		// Show overlays
		["shopify-chat", "axeptio_overlay"].forEach((id) => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = "";
		});

		cartEmit("closed");
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {},
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll("[data-property]");
		propertyElements.forEach((el) => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll("[data-variant-option]");
		variantElements.forEach((el) => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach((key) => {
			if (!["cartItemId", "lineIndex", "maxQuantity"].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;

		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;

		if (!skipEvent) {
			cartEmit("quantity-changed", { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;

		cartEmit("quantity-sync-start", { lineIndex, quantity: newQty, variantId });

		try {
			const sectionId = DOM.cart?.id.replace("shopify-section-", "") || "";

			// Shopify cart/change.js requires 1-based line index and proper sections format
			const requestBody = {
				line: parseInt(lineIndex, 10), // Should be 1-based from template
				quantity: newQty,
			};

			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}

			const data = await CartAPI.request("POST", "cart/change.js", requestBody);

			// processFreeProducts() is disabled because Shopify automatic discounts handle free products
			// The discount applies -100% when the product is added to cart, no need to add it programmatically
			// await this.processFreeProducts(data);

			// Remove loading state from price containers and other loading elements
			window.mmUnloadAll();

			cartEmit("quantity-synced", { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			// Remove loading state even on error
			window.mmUnloadAll();

			cartEmit("quantity-sync-error", { lineIndex, error: error.message });
			// Fallback: refresh cart to sync with server state
			this.get().catch((error) => cartLog("error", "Failed to fetch cart on open", { error: error.message }));
			throw error;
		}
	},

	async mmApplyDiscountInCart() {
		cartLog("info", "mmApplyDiscountInCart called");
		const codeInput = document.getElementById("discount-code");
		const newCode = codeInput.value.trim().toLowerCase();
		const errorMessageText = codeInput.dataset.errorText;
		if (!newCode) {
			cartLog("warn", "No discount code provided");
			return;
		}

		cartLog("info", "Applying discount code", { newCode });

		// Effacer les erreurs précédentes
		const existingError = document.querySelector(".promo-error");
		if (existingError) {
			existingError.remove();
		}

		// Ajouter un état de chargement
		const applyBtn = document.getElementById("apply-discount");
		if (applyBtn) {
			applyBtn.disabled = true;
			applyBtn.classList.add("mm-loading");
		}

		// 1. Récupérer tous les codes déjà appliqués
		let previousCodes = [];
		try {
			const cartBefore = await fetch("/cart.js").then((r) => r.json());
			cartLog("info", "Cart data fetched", { discountCodes: cartBefore.discount_codes });
			// on ne garde que ceux applicables
			previousCodes = cartBefore.discount_codes.filter((d) => d.applicable).map((d) => d.code.toLowerCase());
			cartLog("info", "Previous codes", { previousCodes });
		} catch (err) {
			console.error("Impossible de récupérer les codes existants", err);
		}
	
		// 2. Ajouter le produit lié si besoin (comme avant)
		const hiddenInputs = document.querySelectorAll(".mm-hgjfidu");
		let matchedVariantId = null;
		cartLog("info", "Searching for promo product", { hiddenInputsCount: hiddenInputs.length });
		hiddenInputs.forEach((input) => {
			if (atob(input.value).toLowerCase() === newCode) {
				matchedVariantId = input.dataset.variantId;
			}
		});
		if (matchedVariantId) {
			cartLog("info", "Found promo product, adding to cart", { matchedVariantId });
			try {
				await fetch(window.Shopify.routes.root + "cart/add.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ id: matchedVariantId, quantity: 1 }),
				}).then((r) => r.json());
				cartLog("info", "Promo product added");
			} catch (err) {
				console.error("Erreur ajout produit promo", err);
			}
		}
	
		// 3. Appliquer le code via l'API cart/update.js
		cartLog("info", "Applying discount code via API", { newCode });

		try {
			// Méthode 1: Essayer d'abord avec l'API cart/update.js
			const sectionId = DOM.cart?.id.replace("shopify-section-", "") || "";
			const updatePayload = {
				discount: newCode
			};

			if (sectionId) {
				updatePayload.sections = sectionId;
			}

			const updateResponse = await fetch(window.Shopify.routes.root + "cart/update.js", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(updatePayload),
			});

			if (!updateResponse.ok) {
				throw new Error("Cart update failed");
			}

			const updateData = await updateResponse.json();
			cartLog("info", "Cart updated", { updateData });

			// Vérifier si le code a été appliqué
			const cartAfter = await fetch("/cart.js").then((r) => r.json());
			cartLog("info", "Cart after update", { discountApplications: cartAfter.discount_codes });
			const codeApplied = cartAfter.discount_codes?.find(
				(d) => d.code.toLowerCase() === newCode.toLowerCase()
			);

			if (codeApplied) {
				cartLog("info", "Discount code applied successfully");
				// Stocker le code comme appliqué manuellement
				const manualCodes = JSON.parse(sessionStorage.getItem('manualDiscountCodes') || '[]');
				if (!manualCodes.includes(newCode.toLowerCase())) {
					manualCodes.push(newCode.toLowerCase());
					sessionStorage.setItem('manualDiscountCodes', JSON.stringify(manualCodes));
				}
				// Rafraîchir l'UI avec les nouvelles sections
				if (updateData.sections && updateData.sections[sectionId]) {
					CartAPI.refreshDOM(updateData.sections[sectionId]);
				} else {
					// Fallback: recharger si pas de sections
					window.location.reload();
				}
				// Effacer l'input
				codeInput.value = '';
			} else {
				// Le code n'a pas été appliqué, afficher une erreur
				cartLog("warn", "Discount code not applied");
				this.displayPromoError(errorMessageText);
			}
		} catch (err) {
			console.error("Erreur lors de l'application du code promo", err);
			cartLog("warn", "API failed, falling back to redirect method");

			// Fallback: utiliser la méthode de redirection
			const discountUrl = `${window.Shopify.routes.root}discount/${encodeURIComponent(newCode)}?return_to=/cart`;
			window.location.href = discountUrl;
		} finally {
			// Retirer l'état de chargement
			if (applyBtn) {
				applyBtn.disabled = false;
				applyBtn.classList.remove("mm-loading");
			}
		}
	},
	
	// Affiche un message d'erreur sous le formulaire
	displayPromoError(message) {
		cartLog("info", "Displaying error message", { message });
		let errorElem = document.querySelector(".promo-error");
		if (!errorElem) {
			errorElem = document.createElement("div");
			errorElem.className = "mm-promo-error";
			const form = document.querySelector(".mm-promo-details");
			if (form) {
				form.appendChild(errorElem);
			} else {
				cartLog("error", "Could not find .mm-promo-form to append error");
				return;
			}
		}
		errorElem.textContent = message;
		cartLog("info", "Error message displayed");
	},

	/**
	 * Process free products
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace("shopify-section-", "") || "";
		if (!data.sections?.[sectionId]) return data;

		const temp = document.createElement("div");
		temp.innerHTML = data.sections[sectionId];
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);

		if (!freeProductsEl?.children.length) return data;

		// Get current cart data to check for existing products
		const cartData = await fetch("/cart.js").then(r => r.json()).catch(() => null);
		if (!cartData) return data;

		const updates = {};
		Array.from(freeProductsEl.children).forEach((el) => {
			const variantId = el.dataset.variantId;
			const shouldHave = el.dataset.freeProduct === "true";

			// Check if variant exists in cart (by checking all cart items)
			const existingItem = cartData.items.find(item => item.variant_id.toString() === variantId);
			const hasIt = !!existingItem;

			if (hasIt && !shouldHave) {
				// Product exists but shouldn't be free anymore - remove it
				updates[variantId] = 0;
				cartEmit("free-product-removing", { variantId });
			} else if (!hasIt && shouldHave) {
				// Product should be free but doesn't exist - add it
				updates[variantId] = 1;
				cartEmit("free-product-adding", { variantId });
			}
			// If hasIt && shouldHave - product already exists, do nothing (prevents duplicates)
		});

		if (Object.keys(updates).length > 0) {
			try {
				// Shopify cart/update.js expects updates object and optional sections
				const requestBody = { updates };

				if (sectionId) {
					requestBody.sections = sectionId;
				}

				await CartAPI.request("POST", "cart/update.js", requestBody);
				cartEmit("free-products-updated", { updates });
			} catch (error) {
				cartEmit("free-products-error", { error: error.message });
			}
		}

		return data;
	},

	/**
	 * Add addon to cart
	 */
	async addAddon(variantId, mainItemId) {
		cartEmit("addon-add-start", { variantId, mainItemId });

		try {
			const sectionId = DOM.cart?.id.replace("shopify-section-", "") || "";

			// Create form data for adding addon
			const formData = new FormData();
			formData.append("id", variantId);
			formData.append("quantity", "1");

			// Add custom property to link addon to main product
			formData.append("properties[_addon_for]", mainItemId);

			// Add sections for cart update if available
			if (sectionId) {
				formData.append("sections", sectionId);
			}

			const data = await CartAPI.request("POST", "cart/add.js", formData);

			// Update UI with new cart data (includes DOM refresh)
			CartAPI.updateUI(data);

			// Remove loading state
			window.mmUnloadAll();

			cartEmit("addon-add-success", { variantId, mainItemId });
			return data;
		} catch (error) {
			// Remove loading state even on error
			window.mmUnloadAll();

			cartEmit("addon-add-error", { variantId, mainItemId, error: error.message });
			throw error;
		}
	},

	/**
	 * Remove addon from cart
	 */
	async removeAddon(variantId, mainItemId) {
		cartEmit("addon-remove-start", { variantId, mainItemId });

		try {
			// Find the addon line in cart
			const cartData = await this.get();
			let addonLineIndex = null;

			// Look for addon item with the matching variant ID and property
			cartData.items.forEach((item, index) => {
				if (item.variant_id.toString() === variantId.toString() && item.properties && item.properties._addon_for === mainItemId) {
					addonLineIndex = index + 1; // Shopify uses 1-based indexing
				}
			});

			if (addonLineIndex) {
				// Update quantity to 0 to remove
				await this.updateQuantity(addonLineIndex, 0);
				cartEmit("addon-remove-success", { variantId, mainItemId, lineIndex: addonLineIndex });
			} else {
				throw new Error("Addon item not found in cart");
			}
		} catch (error) {
			cartEmit("addon-remove-error", { variantId, mainItemId, error: error.message });
			throw error;
		}
	},
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = cartDebounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = maxValue && !isNaN(parseInt(maxValue, 10)) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: "increase",
	};

	if (!cartEmit("before-quantity-increase", eventData, true)) return;

	if (newQty > max) {
		cartEmit("quantity-limit-reached", eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit("quantity-increased", { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: "decrease",
	};

	if (!cartEmit("before-quantity-decrease", eventData, true)) return;

	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: "remove",
		};

		if (!cartEmit("before-item-remove", removeEventData, true)) return;

		Cart.setQuantity(lineIndex, 0);
		cartEmit("item-removed", removeEventData);
		debouncedUpdate(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit("quantity-decreased", eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: "destroy",
	};

	if (!cartEmit("before-item-remove", eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit("item-removed", eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = () => Cart.open();
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);
window.mmApplyDiscountInCart = () => Cart.mmApplyDiscountInCart();
window.removeDiscount = async (discountCode = null) => {
	cartLog("info", "Removing discount code", { discountCode });

	try {
		const sectionId = DOM.cart?.id.replace("shopify-section-", "") || "";

		// Retirer le code de la liste des codes manuels
		if (discountCode) {
			const manualCodes = JSON.parse(sessionStorage.getItem('manualDiscountCodes') || '[]');
			const updatedCodes = manualCodes.filter(code => code !== discountCode.toLowerCase());
			sessionStorage.setItem('manualDiscountCodes', JSON.stringify(updatedCodes));
		}

		// Utiliser l'API cart/update.js avec un discount vide pour supprimer
		const updatePayload = {
			discount: "" // Chaîne vide pour supprimer le code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + "cart/update.js", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(updatePayload),
		});

		if (!response.ok) {
			throw new Error("Failed to remove discount");
		}

		const data = await response.json();
		cartLog("info", "Discount removed", { data });

		// Rafraîchir l'UI
		if (data.sections && data.sections[sectionId]) {
			CartAPI.refreshDOM(data.sections[sectionId]);
		} else {
			// Fallback: recharger la page
			window.location.reload();
		}
	} catch (err) {
		console.error("Erreur lors de la suppression du code promo", err);
		// Fallback: recharger la page
		window.location.reload();
	}
};

/*********************************************************************************************
 * 🛒 ADDON FUNCTIONALITY
 */

/**
 * Toggles addon product in cart when checkbox is changed
 */
window.mmToggleAddon = async (checkboxElement) => {
	const isChecked = checkboxElement.checked;
	const addonVariantId = checkboxElement.dataset.addonVariantId;
	const addonProductId = checkboxElement.dataset.addonProductId;
	const mainItemId = checkboxElement.dataset.mainItemId;

	cartEmit("addon-toggle-start", {
		isChecked,
		addonVariantId,
		addonProductId,
		mainItemId,
	});

	try {
		if (isChecked) {
			// Add addon to cart
			await Cart.addAddon(addonVariantId, mainItemId);
			cartEmit("addon-added", { addonVariantId, mainItemId });
		} else {
			// Remove addon from cart
			await Cart.removeAddon(addonVariantId, mainItemId);
			cartEmit("addon-removed", { addonVariantId, mainItemId });
		}
	} catch (error) {
		// Revert checkbox state on error
		checkboxElement.checked = !isChecked;
		cartEmit("addon-toggle-error", {
			error: error.message,
			addonVariantId,
			mainItemId,
		});
		cartLog("error", "Addon toggle failed", {
			error: error.message,
			addonVariantId,
			mainItemId,
		});
	}
};

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Adds loading state to elements
window.mmLoadThis = (element) => {
	if (element) {
		element.classList.add("mm-loading");
		cartEmit("checkout-loading", { element });
	}
};

// Removes loading state from elements
window.mmUnloadThis = (element) => {
	if (element) {
		element.classList.remove("mm-loading");
		cartEmit("loading-removed", { element });
	}
};

// Remove loading state from all elements with mm-loading class
window.mmUnloadAll = () => {
	document.querySelectorAll(".mm-loading").forEach((element) => {
		element.classList.remove("mm-loading");
	});
	cartEmit("all-loading-removed");
};

// Update discount remove buttons visibility based on manual codes
const updateDiscountRemoveButtons = () => {
	const manualCodes = JSON.parse(sessionStorage.getItem('manualDiscountCodes') || '[]');

	document.querySelectorAll('[data-discount-code]').forEach((discountEl) => {
		const discountCode = discountEl.dataset.discountCode;
		const removeBtn = discountEl.querySelector('[data-discount-remove]');
		const titleSpan = discountEl.querySelector('[data-discount-title]');

		if (removeBtn && titleSpan && discountCode) {
			// Afficher le bouton avec croix si le code est manuel, sinon afficher le span simple
			if (manualCodes.includes(discountCode.toLowerCase())) {
				removeBtn.style.display = '';
				titleSpan.style.display = 'none';
			} else {
				removeBtn.style.display = 'none';
				titleSpan.style.display = '';
			}
		}
	});

	cartLog("info", "Discount remove buttons updated", { manualCodes });
};

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener("click", (e) => {
		if (e.target.closest("[data-cart-close]")) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest("[data-checkout-btn]")) {
			window.mmLoadThis(e.target.closest("[data-checkout-btn]"));
		}

		// Apply discount code
		if (e.target.closest("[data-action='apply-discount']")) {
			e.preventDefault();
			Cart.mmApplyDiscountInCart();
		}
	});

	// Update discount remove buttons on init
	updateDiscountRemoveButtons();
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach((form) => {
		form.setAttribute("data-cart-handler-attached", "true");
		form.addEventListener("submit", async (e) => {
			e.preventDefault();

			const submitButton = form.querySelector('[type="submit"]');
			const formData = new FormData(form);

			if (!formData.get("id")) {
				cartLog("error", "No variant ID found in form", { form });
				return;
			}

			// Add loading state
			if (submitButton) {
				submitButton.classList.add("mm-loading");
				submitButton.disabled = true;
			}

			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get("id"),
				quantity: parseInt(formData.get("quantity") || 1, 10),
				properties: {},
				options: {},
				formEntries: {},
			};

			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;

				// Extract properties (custom fields)
				if (key.startsWith("properties[")) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}

				// Extract options (variant selections like size, color)
				if (key.startsWith("option")) {
					extractedData.options[key] = value;
				}
			}

			cartEmit("add-to-cart-start", extractedData);

			cartLog("info", "Adding product to cart", {
				variantId: extractedData.variantId,
				quantity: extractedData.quantity,
				form: form
			});

			try {
				const sectionId = DOM.cart?.id.replace("shopify-section-", "") || "";

				// Add sections for cart update if available
				if (sectionId) {
					formData.append("sections", sectionId);
				}

				const response = await CartAPI.request("POST", "cart/add.js", formData);

				cartLog("info", "Product added successfully", {
					variantId: extractedData.variantId,
					itemCount: response.item_count
				});

				cartEmit("add-to-cart-success", {
					...extractedData,
					response: response,
				});

				// Auto-open cart after successful add (only if not on cart page)
				const isCartPage = window.location.pathname.includes('/cart');
				if (!isCartPage) {
					Cart.open();
				}
			} catch (error) {
				cartEmit("add-to-cart-error", {
					...extractedData,
					error: error.message,
				});
				cartLog("error", "Add to cart failed", {
					error: error.message,
					...extractedData,
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove("mm-loading");
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 STICKY CTA MANAGEMENT
 */
let stickyObserver = null;

const initStickyCTA = () => {
	const mainCheckoutBtn = document.getElementById('checkout');
	const stickyCTA = document.getElementById('sticky-cta');

	// Nettoyer l'ancien observer si existant
	if (stickyObserver) {
		stickyObserver.disconnect();
		stickyObserver = null;
	}

	if (mainCheckoutBtn && stickyCTA && window.innerWidth <= 990) {
		stickyObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					// Si le CTA principal n'est PAS visible, montrer le sticky
					stickyCTA.style.display = entry.isIntersecting ? 'none' : 'block';
				});
			},
			{
				threshold: 0,
				rootMargin: '0px 0px -50px 0px'
			}
		);

		stickyObserver.observe(mainCheckoutBtn);
		cartLog("info", "Sticky CTA observer initialized");
	}
};

const updateStickyPrice = (totalPrice) => {
	const stickyTotalElem = document.querySelector('[data-sticky-total]');
	if (stickyTotalElem && totalPrice) {
		// Formater le prix (Shopify money format)
		const formatted = new Intl.NumberFormat('fr-FR', {
			style: 'currency',
			currency: 'EUR',
			minimumFractionDigits: 2
		}).format(totalPrice / 100);
		stickyTotalElem.textContent = formatted.replace(',00', '');
	}
};

const updateStickyCheckoutURL = (discountCodes) => {
	const stickyCheckout = document.querySelector('[data-sticky-checkout]');
	if (stickyCheckout) {
		let url = window.Shopify.routes.root + 'checkout';
		if (discountCodes && discountCodes.length > 0) {
			const codes = discountCodes.map(d => d.title || d.code).join(',');
			url += '?discount=' + encodeURIComponent(codes);
		}
		stickyCheckout.href = url;
	}
};

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener("DOMContentLoaded", () => {
	cartEmit("initializing");

	Cart.get()
		.then((data) => {
			cartEmit("initialized", { itemCount: data.item_count, totalPrice: data.total_price });
			CartAPI.initCarousels();
			initCartEventListeners();
			window.initAddToCartForms();

			// Forcer l'ouverture du details promo en mobile
			const promoDetails = document.querySelector('.mm-mobile-always-open');
			if (promoDetails && window.innerWidth <= 990) {
				promoDetails.setAttribute('open', '');
			}

			// Garder ouvert au resize
			window.addEventListener('resize', () => {
				if (promoDetails && window.innerWidth <= 990) {
					promoDetails.setAttribute('open', '');
				}
			});

			// Initialiser le sticky CTA
			initStickyCTA();
			updateStickyPrice(data.total_price);
			updateStickyCheckoutURL(data.cart_level_discount_applications || []);
		})
		.catch((error) => {
			cartLog("error", "Cart initialization failed", { error: error.message });
			cartEmit("initialization-error", { error: error.message });
		});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (CART_CONFIG.isDevelopment) {
	["opened", "closed", "quantity-increased", "quantity-decreased", "free-product-adding", "count-updated", "add-to-cart-start", "add-to-cart-success", "add-to-cart-error"].forEach((event) => {
		document.addEventListener(`cart:${event}`, (e) => console.log(`🛒 [CART] ${event}:`, e.detail));
	});
}
